import { Button, Input, Typography } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { InfoIcon } from 'BreetAssets';
import { Checkbox, ModalParts, Tooltip, TooltipContent } from 'BreetComponents';
import {
	handleApiError,
	type IDropDownItem,
	type ITeamMembersRole,
	onFormPreventDefault,
	showToast,
	TEAM_MEMBER_ROLE_AND_PERMISSIONS,
} from 'BreetHelpers';
import { useFormData, useIsSandboxApi } from 'BreetHooks';
import { selectUserData, useAppSelector, useInviteTeamMemberMutation, useUpdateTeamMemberByIdMutation } from 'BreetRedux';
import { type MouseEvent, useCallback, useMemo } from 'react';

import DropDown from '../Dropdown/Dropdown';
import {
	type IInviteTeamMemberChild,
	InviteTeamMemberSchema,
	type InviteTeamMemberSchemaType,
	roleOptions,
	statusOptions,
	TEAM_MEMBER_PERMISSIONS,
} from './DialogInviteTeamMember.helpers';

const Child = ({ teamMemberData, modalApis }: IInviteTeamMemberChild) => {
	const [inviteTeamMemberApi, { isLoading: isInviteLoading }] = useInviteTeamMemberMutation();
	const [updateTeamMemberApi, { isLoading: isUpdateLoading }] = useUpdateTeamMemberByIdMutation();

	const { isSandbox } = useIsSandboxApi();

	const { closeModalDirect, getModalProps, handleOpenModal } = modalApis;
	const modalProps = getModalProps('inviteTeamMember');
	const modalMetadata = modalProps?.metadata as { intent?: 'invite' | 'view'; teamMemberId?: string } | undefined;

	const userData = useAppSelector(selectUserData);

	const isAdmin = userData?.businessRole === 'owner' || userData?.businessRole === 'super_admin';

	const actorRole = userData?.businessRole as 'super_admin' | 'owner' | undefined;
	const targetRole = teamMemberData?.data?.role;
	const isSelf = userData?.email === teamMemberData?.data?.email;

	const actorRules = actorRole ? TEAM_MEMBER_PERMISSIONS[actorRole] : null;

	const activePermissions = useMemo(() => {
		if (!actorRules) return [];

		if (isSelf) {
			return actorRules.self;
		}

		if (actorRole === 'super_admin' && targetRole === 'owner') {
			return actorRules.owner ?? [];
		}

		return actorRules.all;
	}, [actorRules, isSelf, actorRole, targetRole]);

	const canEdit = activePermissions.includes('edit');
	const canDelete = activePermissions.includes('delete');

	const inviteTeamMemberFormData = useFormData(
		InviteTeamMemberSchema,
		{
			email: teamMemberData?.data?.email,
			fullName: teamMemberData?.data?.fullName,
			role: teamMemberData?.data?.role ?? 'developer',
		},
		{ mode: 'onChange' }
	);

	const teamMemberRole = inviteTeamMemberFormData.hookFormProps.watch('role');

	const isRoleChanged = teamMemberRole !== teamMemberData?.data?.role;

	const handleSelectRole = useCallback(
		(item: IDropDownItem) => (e: MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			inviteTeamMemberFormData.hookFormProps.setValue('role', item.value, { shouldValidate: true });
		},
		[inviteTeamMemberFormData.hookFormProps]
	);

	const selectedRoleItem = roleOptions.find((option) => option.value === inviteTeamMemberFormData.hookFormProps.watch('role'));
	const selectedStatusItem = statusOptions.find((option) => option.value === teamMemberData?.data?.status);

	const modalTitle = modalMetadata?.intent === 'invite' ? 'Invite Team Member' : 'Team Member';

	const selectedRole = (
		modalMetadata?.intent === 'invite' || isAdmin
			? inviteTeamMemberFormData.hookFormProps.watch('role')
			: teamMemberData?.data?.role
	) as ITeamMembersRole | undefined;

	const permissionOptions = useMemo(() => {
		if (!selectedRole) return [];
		return TEAM_MEMBER_ROLE_AND_PERMISSIONS[selectedRole];
	}, [selectedRole]);

	const isBtnDisabled =
		!inviteTeamMemberFormData.hookFormProps.formState.isValid || !inviteTeamMemberFormData.hookFormProps.formState.isDirty;

	const inviteTeamMember = useCallback(
		(data: InviteTeamMemberSchemaType) => {
			inviteTeamMemberApi({
				email: data.email,
				fullName: data.fullName,
				role: data.role,
			})
				.unwrap()
				.then(() => {
					closeModalDirect('inviteTeamMember');
				})
				.catch(handleApiError);
		},
		[inviteTeamMemberApi, closeModalDirect]
	);

	const updateTeamMember = useCallback(() => {
		if (!teamMemberData?.data?.id) {
			showToast('error', 'Team member not found');
			return;
		}

		updateTeamMemberApi({
			id: teamMemberData.data.id,
			role: teamMemberRole,
		})
			.unwrap()
			.then(() => {
				showToast('success', 'Team member updated successfully');
				closeModalDirect('inviteTeamMember');
			})
			.catch(handleApiError);
	}, [teamMemberData?.data?.id, updateTeamMemberApi, teamMemberRole, closeModalDirect]);

	return (
		<ModalParts.Wrapper
			title={modalTitle}
			root={modalTitle}
			rightSheet
		>
			<div className={classNameMerger('invite-team-member', modalMetadata?.intent === 'view' && !isAdmin && 'view-intent')}>
				{modalMetadata?.intent === 'invite' && (
					<Typography
						as='h4'
						className='color-primary-100'
					>
						Enter the details of your team member
					</Typography>
				)}

				<form
					className='invite-team-member-form new-drop-down-input'
					data-modal-ignore='true'
					onSubmit={onFormPreventDefault}
				>
					<Input
						id=''
						label='Email Address'
						widthType='full'
						required
						{...inviteTeamMemberFormData.hookFormProps.register('email')}
						readOnly={modalMetadata?.intent === 'view'}
						errorMessage={inviteTeamMemberFormData.hookFormProps.formState.errors.email?.message}
					/>

					<Input
						id=''
						label='Full Name'
						widthType='full'
						required
						{...inviteTeamMemberFormData.hookFormProps.register('fullName')}
						readOnly={modalMetadata?.intent === 'view'}
						errorMessage={inviteTeamMemberFormData.hookFormProps.formState.errors.fullName?.message}
					/>

					<DropDown.Default
						handleItemSelect={handleSelectRole}
						selectedItem={
							selectedRoleItem
								? {
										...selectedRoleItem,
										value: selectedRoleItem.name,
									}
								: undefined
						}
						label='Select Role'
						id='role'
						items={roleOptions.filter((i) => i.value !== 'owner')}
						iconAs='image'
						placeholder='Select Role'
						hideValue
						errorMessage={inviteTeamMemberFormData.hookFormProps.formState.errors.role?.message}
						readOnly={!canEdit || isSandbox}
					/>

					<div className='permission bg-primary-600 border-dark-navy'>
						<div className='permission-header label-tooltip-header'>
							<Typography className='color-tertiary-400'>Permissions</Typography>
							<Tooltip
								tipElement={
									<TooltipContent content='Permissions define the actions a team member is allowed to perform within your workspace based on their assigned role.' />
								}
							>
								<InfoIcon className='info-icon svg-dark' />
							</Tooltip>
						</div>
						<div className='permission-list'>
							{permissionOptions.map((item) => (
								<div
									key={item.label}
									className='permission-item label-tooltip-header'
								>
									<Checkbox
										checked
										disabled
									>
										<Typography className='color-neutral-50'>{item.label}</Typography>
									</Checkbox>
									<Tooltip tipElement={<TooltipContent content={item.description} />}>
										<InfoIcon className='info-icon svg-dark' />
									</Tooltip>
								</div>
							))}
						</div>
					</div>

					{modalMetadata?.intent === 'view' && !isAdmin && (
						<div className={classNameMerger('drop-down-status', teamMemberData?.data?.status)}>
							<DropDown.Default
								handleItemSelect={handleSelectRole}
								selectedItem={
									selectedStatusItem
										? {
												...selectedStatusItem,
												value: selectedStatusItem.name,
											}
										: undefined
								}
								label='Status'
								id='status'
								items={statusOptions}
								iconAs='image'
								placeholder='Status'
								hideValue
								errorMessage={inviteTeamMemberFormData.hookFormProps.formState.errors.role?.message}
								readOnly
							/>
						</div>
					)}

					{teamMemberData?.data?.role !== 'owner' && canEdit && !isSandbox && (
						<div className='cta-button'>
							<Button
								disabled={modalMetadata?.intent === 'view' && isAdmin ? !isRoleChanged : isBtnDisabled}
								type='button'
								onClick={
									modalMetadata?.intent === 'view' && isAdmin
										? updateTeamMember
										: inviteTeamMemberFormData.hookFormProps.handleSubmit(inviteTeamMember)
								}
								isLoading={isUpdateLoading || isInviteLoading}
							>
								{modalMetadata?.intent === 'view' ? 'Save Changes' : 'Invite'}
							</Button>
						</div>
					)}

					{modalMetadata?.intent === 'view' && canDelete && !isSandbox && (
						<div className='cta-button-remove'>
							<Button
								variant='native'
								type='button'
								onClick={handleOpenModal({ id: 'deleteTeamMember', metadata: { teamMemberId: teamMemberData?.data?.id } })}
							>
								Remove Team Member
							</Button>
						</div>
					)}
				</form>
			</div>
		</ModalParts.Wrapper>
	);
};

export default Child;
